<template>
	<div class="settings-panel" :class="{ 'settings-panel--mobile': isMobile }" @click.stop>
		<div class="settings-list">
			<div class="settings-row settings-row--switch" @click.stop>
				<span class="settings-label">好友验证</span>
				<el-switch
					v-model="userInfo.isManualApprove"
					:disabled="pendingKeys.manualApprove"
					@change="onManualApproveChange"
				></el-switch>
			</div>
			<div class="settings-row settings-row--switch" @click.stop>
				<span class="settings-label">消息提示音</span>
				<el-switch
					v-model="userInfo.isAudioTip"
					:disabled="pendingKeys.audioTip"
					@change="onMessageAudioTip"
				></el-switch>
			</div>

			<button type="button" class="settings-row settings-row--action" @click="openPasswordDialog">
				<span class="settings-label">修改密码</span>
				<i class="el-icon-arrow-right"></i>
			</button>
			<button type="button" class="settings-row settings-row--action" @click="handleManualCheck">
				<span class="settings-label">检查更新</span>
				<span class="settings-trailing">
					<span v-if="currentVersionDisplay" class="settings-version">v{{ currentVersionDisplay }}</span>
					<i :class="manualChecking ? 'el-icon-loading' : 'el-icon-arrow-right'"></i>
				</span>
			</button>
			<button v-if="isElectron" type="button" class="settings-row settings-row--action" @click="openAppLogDirectory">
				<span class="settings-label">打开日志目录</span>
				<i class="el-icon-folder-opened"></i>
			</button>
			<button type="button" class="settings-row settings-row--action" @click="deviceCheckVisible = true">
				<span class="settings-label">设备检测</span>
				<i class="el-icon-setting"></i>
			</button>
			<button
				type="button"
				class="settings-row settings-row--action settings-row--warning"
				:disabled="clearingLocalCache"
				@click="handleClearLocalCache"
			>
				<span class="settings-label">清理本地缓存</span>
				<i :class="clearingLocalCache ? 'el-icon-loading' : 'el-icon-delete'"></i>
			</button>

			<div class="settings-divider"></div>

			<button type="button" class="settings-row settings-row--action settings-row--danger" @click="onLogout">
				<span class="settings-label">退出登录</span>
				<i class="el-icon-switch-button"></i>
			</button>
		</div>

		<settings-password-dialog
			:visible.sync="passwordDialogVisible"
			:is-mobile="isMobile"
		></settings-password-dialog>
		<device-check-dialog :visible.sync="deviceCheckVisible" :is-mobile="isMobile"></device-check-dialog>
	</div>
</template>

<script>
import SettingsPasswordDialog from './SettingsPasswordDialog.vue';
import DeviceCheckDialog from './DeviceCheckDialog.vue';
import packageJson from '../../../package.json';
import { openAppLogDirectory as openLogDirectory } from '@/utils/appLogger';

export default {
	name: 'SettingsPanel',
	components: {
		SettingsPasswordDialog,
		DeviceCheckDialog
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		isMobile: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			passwordDialogVisible: false,
			deviceCheckVisible: false,
			lastManualPhaseKey: '',
			userInfo: {
				isManualApprove: false,
				isAudioTip: false
			},
			clearingLocalCache: false,
			pendingKeys: {
				manualApprove: false,
				audioTip: false
			}
		};
	},
	computed: {
		updater() {
			return this.configStore && this.configStore.updater ? this.configStore.updater : {};
		},
		manualChecking() {
			return this.updater.checking && this.updater.trigger === 'manual';
		},
		currentVersionDisplay() {
			return this.updater.currentVersion || process.env.VUE_APP_VERSION || packageJson.version || '';
		},
		isElectron() {
			return Boolean(this.configStore && this.configStore.electronMode);
		}
	},
	watch: {
		visible(nextVisible) {
			if (nextVisible) {
				this.syncUserInfo();
				this.refreshCurrentVersion();
			}
		},
		'configStore.updater.phase': {
			handler(nextPhase) {
				const updater = this.updater || {};
				if (updater.trigger !== 'manual') {
					return;
				}
				const phaseKey = `${nextPhase || ''}:${updater.latestVersion || ''}:${updater.errorMessage || ''}`;
				if (!nextPhase || phaseKey === this.lastManualPhaseKey) {
					return;
				}
				this.lastManualPhaseKey = phaseKey;
				if (nextPhase === 'not-available') {
					this.$message.success('当前应用已是最新版本');
				} else if (nextPhase === 'error') {
					this.$message.error(updater.errorMessage || '检查应用更新失败');
				}
			}
		}
	},
	methods: {
		syncUserInfo() {
			const mine = (this.userStore && this.userStore.userInfo) || {};
			this.userInfo = {
				isManualApprove: Boolean(mine.isManualApprove),
				isAudioTip: Boolean(mine.isAudioTip)
			};
		},
		refreshCurrentVersion() {
			if (!this.configStore || !this.configStore.electronMode || !this.$desktopUpdater) {
				return;
			}
			if (typeof this.$desktopUpdater.updateCurrentVersion !== 'function') {
				return;
			}
			this.$desktopUpdater.updateCurrentVersion().catch(() => {});
		},
		async updateToggleSetting(options) {
			const {
				localKey,
				pendingKey,
				url,
				successText,
				afterSuccess
			} = options;
			const currentValue = this.userInfo[localKey];
			this.pendingKeys[pendingKey] = true;
			try {
				await this.$http({
					url,
					method: 'put'
				});
				if (afterSuccess) {
					await afterSuccess(currentValue);
				}
				if (successText) {
					this.$message.success(successText(currentValue));
				}
			} catch (error) {
				this.userInfo[localKey] = !currentValue;
				this.$message.error(error && error.message ? error.message : '设置失败，请重试');
			} finally {
				this.pendingKeys[pendingKey] = false;
			}
		},
		onManualApproveChange() {
			this.updateToggleSetting({
				localKey: 'isManualApprove',
				pendingKey: 'manualApprove',
				url: `/user/manualApprove?enabled=${this.userInfo.isManualApprove}`,
				successText: (enabled) => enabled ? '已开启好友验证' : '已关闭好友验证',
				afterSuccess: (enabled) => {
					this.userStore.userInfo.isManualApprove = enabled;
				}
			});
		},
		onMessageAudioTip() {
			this.updateToggleSetting({
				localKey: 'isAudioTip',
				pendingKey: 'audioTip',
				url: `/user/audioTip?enabled=${this.userInfo.isAudioTip}`,
				successText: (enabled) => enabled ? '已开启消息提示音' : '已关闭消息提示音',
				afterSuccess: (enabled) => {
					this.userStore.userInfo.isAudioTip = enabled;
				}
			});
		},
		openPasswordDialog() {
			this.passwordDialogVisible = true;
			this.$nextTick(() => {
				this.$emit('request-close');
			});
		},
		handleManualCheck() {
			this.$emit('request-close');
			if (!this.configStore || !this.configStore.electronMode || !this.$desktopUpdater) {
				this.$message.info('当前不是应用桌面端');
				return;
			}
			this.lastManualPhaseKey = '';
			this.$desktopUpdater.checkForUpdate('manual').catch((error) => {
				this.$message.error(error && error.message ? error.message : '检查应用更新失败');
			});
		},
		async openAppLogDirectory() {
			try {
				await openLogDirectory();
				this.$emit('request-close');
			} catch (error) {
				this.$message.error(error && error.message ? error.message : '打开日志目录失败');
			}
		},
		async handleClearLocalCache() {
			if (this.clearingLocalCache) {
				return;
			}
			try {
				await this.$confirm(
					'将清理当前账号在本地保存的会话消息、好友/群组同步缓存、好友分组和临时媒体缓存。是否继续？',
					'清理本地缓存',
					{
						type: 'warning',
						confirmButtonText: '清理',
						cancelButtonText: '取消',
						closeOnClickModal: false,
						closeOnPressEscape: false
					}
				);
			} catch (error) {
				return;
			}
			this.clearingLocalCache = true;
			try {
				await new Promise((resolve, reject) => {
					this.$emit('clear-local-cache', { resolve, reject });
				});
				this.$message.success('本地缓存已清理');
				this.$emit('request-close');
			} catch (error) {
				this.$message.error(error && error.message ? error.message : '清理本地缓存失败，请稍后重试');
			} finally {
				this.clearingLocalCache = false;
			}
		},
		onLogout() {
			this.$emit('logout');
		}
	}
};
</script>

<style scoped lang="scss">
.settings-panel {
	width: 100%;
	min-width: 0;

	.settings-list {
		display: flex;
		flex-direction: column;
	}

	.settings-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		min-height: 28px;
		padding: 0 6px;
		border: none;
		border-radius: 6px;
		background: transparent;
		color: var(--im-text-color);
		text-align: left;
		transition: background-color 0.2s ease, color 0.2s ease;

		.settings-label {
			font-size: 12px;
			line-height: 1.25;
		}

		i {
			flex-shrink: 0;
			font-size: 15px;
			color: var(--im-text-color-light);
		}

		.settings-trailing {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			flex-shrink: 0;
			color: var(--im-text-color-light);
		}

		.settings-version {
			max-width: 96px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			font-size: 11px;
			line-height: 1.2;
		}
	}

	.settings-row--action {
		cursor: pointer;
		appearance: none;
		width: 100%;

		&:hover {
			background: rgba(var(--im-primary-rgb), 0.08);
		}
	}

	.settings-row--danger {
		color: var(--im-color-danger);

		i {
			color: inherit;
		}

		&:hover {
			background: rgba(var(--im-danger-rgb), 0.08);
		}
	}

	.settings-row--warning {
		color: var(--im-color-warning);

		i {
			color: inherit;
		}

		&:hover {
			background: rgba(var(--im-warning-rgb), 0.08);
		}

		&:disabled {
			cursor: default;
			opacity: 0.74;
		}
	}

	.settings-divider {
		height: 1px;
		margin: 6px;
		background: var(--im-surface-panel-border);
	}

	&.settings-panel--mobile {
		height: 100%;

		.settings-list {
			padding: 8px 10px 14px;
		}

		.settings-row {
			min-height: 46px;
			border-radius: 12px;
		}
	}
}
</style>
